import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DigitalScoreRing, DigitalTierBadge } from '../components/DigitalScore';
import { Shield, CheckCircle2, XCircle, Globe, Search, ChevronLeft, ChevronRight, ExternalLink, Eye, AlertTriangle, Wifi, MapPin } from 'lucide-react';
import { ClaimsQueue } from '../components/ClaimsQueue';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'not_checked', label: 'Not Checked' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'verified_live', label: 'Verified Live' },
  { value: 'no_domain_found', label: 'No Domain Found' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'manually_verified', label: 'Manually Verified' },
];

const STATUS_COLORS = {
  'verified_live': 'bg-status-green/10 text-status-green',
  'manually_verified': 'bg-status-green/10 text-status-green',
  'pending_verification': 'bg-status-amber/10 text-status-amber',
  'not_checked': 'bg-ink/5 text-ink/40',
  'no_domain_found': 'bg-status-red/10 text-status-red',
  'flagged': 'bg-brand-orange/10 text-brand-orange',
};

export default function VerificationQueue() {
  const { user } = useAuth();
  const [data, setData] = useState({ businesses: [], total: 0, page: 1, pages: 1, stats: {} });
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifyBiz, setVerifyBiz] = useState(null);
  const [verifyForm, setVerifyForm] = useState({});
  const [verifyStats, setVerifyStats] = useState(null);
  const [reverifyStatus, setReverifyStatus] = useState(null);
  const [reverifying, setReverifying] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [queue, stats, rvStatus] = await Promise.all([
        api.get(`/admin/verification-queue?status=${status === 'all' ? '' : status}&page=${p}&limit=15`),
        api.get('/admin/verification-stats'),
        api.get('/admin/reverify-status'),
      ]);
      setData(queue.data);
      setVerifyStats(stats.data);
      setReverifyStatus(rvStatus.data);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(1); }, [load]);

  const handleReverify = async () => {
    setReverifying(true);
    try {
      await api.post('/admin/re-verify');
      load(page);
    } catch (e) { console.error(e); }
    finally { setReverifying(false); }
  };

  const handleVerify = async () => {
    if (!verifyBiz) return;
    try {
      await api.put(`/admin/verify-business/${verifyBiz.id}`, verifyForm);
      setVerifyBiz(null);
      setVerifyForm({});
      load(page);
    } catch (e) { console.error(e); }
  };

  const openVerify = (biz) => {
    setVerifyBiz(biz);
    setVerifyForm({
      website: biz.website || false,
      google_business: biz.google_business || false,
      directory_listing: biz.directory_listing || false,
      social_media: biz.social_media || false,
      reviews: biz.reviews || false,
      verified_domain: biz.verified_domain || '',
      notes: '',
    });
  };

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-[1200px] mx-auto px-4 py-12 text-center">
        <Shield className="w-12 h-12 text-ink/15 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-extrabold text-ink mb-2">Admin Access Required</h1>
      </div>
    );
  }

  const stats = data.stats || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-5" data-testid="verification-queue">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink flex items-center gap-2">
            <Eye className="w-6 h-6 text-brand-orange" strokeWidth={1.5} /> Verification Queue
          </h1>
          <p className="font-body text-xs text-ink/50 mt-1">Spot-check and audit estimated digital footprint scores</p>
        </div>
        <Button onClick={handleReverify} disabled={reverifying} variant="outline" className="font-body text-xs rounded-[4px]" data-testid="reverify-btn">
          {reverifying ? 'Re-verifying...' : `Re-verify Stale (${reverifyStatus?.stale_count || 0})`}
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { label: 'Queue', value: stats.total_queue || 0, color: 'text-ink' },
          { label: 'Verified Live', value: stats.verified_live || 0, color: 'text-status-green' },
          { label: 'Pending', value: stats.pending || 0, color: 'text-status-amber' },
          { label: 'No Domain', value: stats.no_domain || 0, color: 'text-status-red' },
          { label: 'Not Checked', value: stats.not_checked || 0, color: 'text-ink/40' },
          { label: 'Manually Done', value: stats.manually_verified || 0, color: 'text-ai-purple' },
        ].map((s, i) => (
          <div key={s.label} className="solid-card p-3 text-center" data-testid={`vq-stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
            <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">{s.label}</span>
            <span className={`font-display text-xl font-extrabold ${s.color}`}>{s.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>

      {/* Business profile ownership claims */}
      <ClaimsQueue />

      {/* Verification method stats */}
      {verifyStats?.by_method && (
        <div className="glass-panel p-4">
          <span className="font-body text-[10px] uppercase tracking-[0.12em] text-ink/50 font-medium block mb-2">Verification Methods</span>
          <div className="flex flex-wrap gap-3">
            {verifyStats.by_method.map((m, i) => (
              <div key={m.method} className="flex items-center gap-2 font-body text-xs text-ink/70">
                <span className="w-2 h-2 rounded-full bg-brand-orange" />
                <span>{m.method}: {m.count} businesses (avg {m.avg_score}/5)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3 items-end">
        <div className="w-[220px]">
          <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40">Filter by Status</span>
          <Select value={status} onValueChange={v => setStatus(v)}>
            <SelectTrigger data-testid="vq-status-filter" className="font-body text-xs mt-1 h-8"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="font-body text-xs">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <span className="font-body text-xs text-ink/40 mb-1">{data.total?.toLocaleString()} businesses · Page {page}/{data.pages}</span>
      </div>

      {/* Queue list */}
      <div className="space-y-2">
        {loading ? (
          <div className="solid-card p-8 text-center"><span className="font-body text-sm text-ink/40">Loading...</span></div>
        ) : data.businesses?.length === 0 ? (
          <div className="solid-card p-8 text-center"><CheckCircle2 className="w-8 h-8 text-status-green/30 mx-auto mb-2" /><p className="font-body text-sm text-ink/40">No businesses matching this filter</p></div>
        ) : data.businesses.map(biz => (
          <div key={biz.id} className="solid-card p-3 flex items-center gap-3 hover:-translate-y-[1px] transition-all" data-testid={`vq-item-${biz.id}`}>
            {/* Score */}
            <div className="shrink-0 w-12 text-center">
              <span className="font-display text-xl font-extrabold" style={{ color: biz.digital_score >= 4 ? '#2D6E4E' : biz.digital_score >= 2 ? '#8A5A14' : '#8A1A1A' }}>
                {biz.digital_score}
              </span>
              <span className="font-body text-[10px] text-ink/30 block">/5</span>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-body text-sm text-ink font-medium truncate">{biz.company_name}</span>
                <DigitalTierBadge tier={biz.digital_tier} />
                <span className={`text-[10px] font-body px-1.5 py-0.5 rounded-[4px] uppercase tracking-wider ${STATUS_COLORS[biz.website_check_status] || 'bg-ink/5 text-ink/40'}`}>
                  {(biz.website_check_status || 'unknown').replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 font-body text-[11px] text-ink/40">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {biz.postcode}</span>
                <span>{biz.sector}</span>
                <span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> {biz.broadband_type || '—'}</span>
                {biz.verified_domain && (
                  <span className="flex items-center gap-1 text-status-green"><Globe className="w-3 h-3" /> {biz.verified_domain}</span>
                )}
              </div>
              {/* Footprint indicators */}
              <div className="flex gap-1.5 mt-1.5">
                {[
                  { key: 'website', label: 'Web' },
                  { key: 'google_business', label: 'GMB' },
                  { key: 'directory_listing', label: 'Dir' },
                  { key: 'social_media', label: 'Soc' },
                  { key: 'reviews', label: 'Rev' },
                ].map(fp => (
                  <span key={fp.key} className={`text-[9px] font-body px-1 py-0.5 rounded-[2px] ${biz[fp.key] ? 'bg-status-green/10 text-status-green' : 'bg-ink/5 text-ink/25'}`}>
                    {fp.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Confidence */}
            <div className="shrink-0 text-right">
              <span className="font-body text-[10px] text-ink/40 block">Confidence</span>
              <span className="font-display text-sm font-extrabold text-ink">{Math.round((biz.confidence_score || 0.6) * 100)}%</span>
            </div>

            {/* Verify button */}
            <Button variant="outline" size="sm" onClick={() => openVerify(biz)} className="shrink-0 font-body text-[10px] rounded-[4px] h-7" data-testid={`vq-verify-${biz.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Verify
            </Button>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} className="font-body text-xs rounded-[4px] h-7">
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </Button>
          <span className="font-body text-xs text-ink/40">Page {page} of {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => load(page + 1)} className="font-body text-xs rounded-[4px] h-7">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Verify Dialog */}
      <Dialog open={!!verifyBiz} onOpenChange={() => setVerifyBiz(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold text-ink">Manual Verification</DialogTitle>
          </DialogHeader>
          {verifyBiz && (
            <div className="space-y-4">
              <div>
                <span className="font-body text-sm font-medium text-ink">{verifyBiz.company_name}</span>
                <span className="font-body text-xs text-ink/40 block">{verifyBiz.postcode} · {verifyBiz.sector} · CH: {verifyBiz.ch_number}</span>
                {verifyBiz.suggested_domains?.length > 0 && (
                  <div className="mt-2">
                    <span className="font-body text-[10px] uppercase tracking-wider text-ink/40">Suggested Domains:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {verifyBiz.suggested_domains.map((d, i) => (
                        <a key={d} href={`https://${d}`} target="_blank" rel="noopener noreferrer" className="font-body text-[10px] text-info-blue underline flex items-center gap-0.5">
                          {d} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <span className="font-body text-xs uppercase tracking-wider text-ink/50">Digital Footprint</span>
                {[
                  { key: 'website', label: 'Website' },
                  { key: 'google_business', label: 'Google Business Profile' },
                  { key: 'directory_listing', label: 'Directory Listing (Yell, etc.)' },
                  { key: 'social_media', label: 'Social Media' },
                  { key: 'reviews', label: 'Customer Reviews' },
                ].map(fp => (
                  <label key={fp.key} className="flex items-center justify-between py-1">
                    <span className="font-body text-sm text-ink/70">{fp.label}</span>
                    <Switch checked={verifyForm[fp.key] || false} onCheckedChange={v => setVerifyForm(f => ({ ...f, [fp.key]: v }))} data-testid={`vf-${fp.key}`} />
                  </label>
                ))}
              </div>

              <div>
                <span className="font-body text-xs uppercase tracking-wider text-ink/50">Verified Domain</span>
                <Input value={verifyForm.verified_domain || ''} onChange={e => setVerifyForm(f => ({ ...f, verified_domain: e.target.value }))} placeholder="e.g. www.example.co.uk" className="font-body text-sm mt-1" data-testid="vf-domain" />
              </div>

              <div>
                <span className="font-body text-xs uppercase tracking-wider text-ink/50">Notes</span>
                <Input value={verifyForm.notes || ''} onChange={e => setVerifyForm(f => ({ ...f, notes: e.target.value }))} placeholder="Verification notes..." className="font-body text-sm mt-1" data-testid="vf-notes" />
              </div>

              <div className="solid-card p-3 text-center">
                <span className="font-body text-xs text-ink/40">Resulting Score</span>
                <span className="font-display text-2xl font-extrabold text-brand-orange block">
                  {[verifyForm.website, verifyForm.google_business, verifyForm.directory_listing, verifyForm.social_media, verifyForm.reviews].filter(Boolean).length}/5
                </span>
              </div>

              <Button onClick={handleVerify} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]" data-testid="vf-submit">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm Verification
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
